import { getAuth } from "firebase/auth";
import {
  collection,
  doc,
  documentId,
  getDocs,
  query,
  where,
  writeBatch,
} from "firebase/firestore";

import {
  organizersAdapter,
  publicProfilesAdapter,
  tournamentsAdapter,
  usersAdapter,
} from "./adapters/firestore";

import { db } from "@/lib/firebase";
import { formatFirestoreDocument } from "@/lib/firestore";
import { usernameFromEmail } from "@/lib/utils";
import { PrivateUser, PublicUser, Tournament, User } from "@/types";

/**
 * Check if user has admin access (either superAdmin or is an organizer)
 * Use this for general admin section access
 */
export const isUserAdmin = (
  user: PrivateUser | User | null | undefined,
): boolean => {
  return Boolean(user && (user.superAdmin || user?.organizerId));
};

// ============================================================================
// Display Name Fallback
// ============================================================================

/**
 * Apply display name fallback for users with empty or placeholder names
 * Client-side version - requires translated fallback name to be passed in
 *
 * @param user - User or PublicUser object
 * @param fallbackName - Translated fallback name (e.g., t("common.player"))
 * @returns User with fallback name applied if needed
 */
export function applyDisplayNameFallback<T extends { name?: string }>(
  user: T,
  fallbackName: string,
): T {
  if (!user.name || user.name === "User" || user.name === "") {
    return {
      ...user,
      name: fallbackName,
    };
  }
  // Never expose a raw email address as a display name (legacy records that
  // stored the full email as the name). Derive a safe username from it.
  if (user.name.includes("@")) {
    return {
      ...user,
      name: usernameFromEmail(user.name) || fallbackName,
    };
  }
  return user;
}

/**
 * Apply display name fallback to an array of users
 *
 * @param users - Array of User or PublicUser objects
 * @param fallbackName - Translated fallback name (e.g., t("common.player"))
 * @returns Array of users with fallback names applied
 */
export function applyDisplayNameFallbackToMany<T extends { name?: string }>(
  users: T[],
  fallbackName: string,
): T[] {
  return users.map((user) => applyDisplayNameFallback(user, fallbackName));
}

// ============================================================================
// Authorization Checks
// ============================================================================

/**
 * Check if user can manage a specific tournament
 * Returns true if user is:
 * - A superAdmin (can manage all tournaments)
 * - The organizer of this specific tournament
 */
export const isTournamentOrganizer = async (
  user: PrivateUser | User | null | undefined,
  tournamentId: Tournament["id"],
): Promise<boolean> => {
  // SuperAdmins can manage all tournaments
  if (user?.superAdmin) return true;

  // Check if user is the organizer of this specific tournament
  if (tournamentId && user?.organizerId) {
    const tournament = await tournamentsAdapter.getById(tournamentId);
    return tournament?.organizerId === user.organizerId;
  }

  return false;
};

/**
 * Get public profile (for displaying team members, etc.)
 * Fetches from publicProfiles collection
 */
export async function getPublicProfile(
  userId: string,
): Promise<PublicUser | null> {
  return publicProfilesAdapter.getById(userId);
}

/**
 * Get user's private data by UID (includes email)
 * Fetches from users collection
 * Use this when you already have the UID (e.g., in AuthContext)
 */
export async function getPrivateUser(
  userId: string,
): Promise<PrivateUser | null> {
  return usersAdapter.getById(userId);
}

/**
 * Get complete user data by UID (public + private)
 * Combines data from both collections
 * Use this when you already have the UID (e.g., in AuthContext)
 */
export async function getUser(userId: string): Promise<User | null> {
  const [privateData, publicData] = await Promise.all([
    getPrivateUser(userId),
    getPublicProfile(userId),
  ]);

  if (!publicData || !privateData) return null;

  // Fetch organizer name if user has organizerId
  let organizerName: string | undefined;
  let organizerSlug: string | undefined;
  if (privateData.organizerId) {
    const organizer = await organizersAdapter.getById(privateData.organizerId);
    organizerName = organizer?.name;
    organizerSlug = organizer?.slug;
  }

  const combinedUser = {
    ...publicData,
    email: privateData.email,
    organizerId: privateData.organizerId,
    organizerName: organizerName,
    organizerSlug: organizerSlug,
    superAdmin: privateData.superAdmin,
    fcmTokens: privateData.fcmTokens,
    pushNotificationsEnabled: privateData.pushNotificationsEnabled,
    notificationPreferences: privateData.notificationPreferences,
  } as User;

  return combinedUser;
}

/**
 * Get current user's private data (includes email)
 * Fetches from users collection using Firebase Auth
 */
export async function getCurrentUserPrivateData(): Promise<PrivateUser | null> {
  const auth = getAuth();

  if (!auth.currentUser) return null;

  return getPrivateUser(auth.currentUser.uid);
}

/**
 * Get complete current user data (public + private)
 * Combines data from both collections using Firebase Auth
 * CLIENT-SIDE ONLY - Use getCurrentUserServer() from lib/users-server.ts for SSR
 */
export async function getCurrentUser(): Promise<User | null> {
  const auth = getAuth();

  if (!auth.currentUser) return null;

  return getUser(auth.currentUser.uid);
}

/**
 * Create new user profile (both collections atomically)
 * Called during user signup
 */
export async function createUserProfile(
  userId: string,
  email: string,
  name: string,
  photoURL?: string,
): Promise<void> {
  const batch = writeBatch(db);
  const now = new Date();

  // Private data (sensitive information)
  batch.set(doc(db, "users", userId), {
    id: userId,
    email: email,
    superAdmin: false,
    createdAt: now,
    updatedAt: now,
  });

  // Public data (safe to share)
  batch.set(doc(db, "publicProfiles", userId), {
    id: userId,
    name: name,
    photoURL: photoURL || "",
    createdAt: now,
    updatedAt: now,
    language: "fi",
  });

  await batch.commit();
}

// ============================================================================
// Batch Fetching Functions
// ============================================================================

/**
 * Get multiple public profiles by user IDs
 * Batches requests to respect Firestore's "in" query limit of 30
 *
 * @param userIds - Array of user IDs to fetch
 * @returns Array of public profiles (excludes not found users)
 */
export async function getPublicProfiles(
  userIds: string[],
): Promise<PublicUser[]> {
  if (userIds.length === 0) {
    return [];
  }

  // Deduplicate IDs
  const uniqueIds = [...new Set(userIds)];

  const BATCH_SIZE = 30;
  const batches: string[][] = [];

  for (let i = 0; i < uniqueIds.length; i += BATCH_SIZE) {
    batches.push(uniqueIds.slice(i, i + BATCH_SIZE));
  }

  const results = await Promise.all(
    batches.map(async (batch) => {
      const q = query(
        collection(db, "publicProfiles"),
        where(documentId(), "in", batch),
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) =>
        formatFirestoreDocument(doc),
      ) as PublicUser[];
    }),
  );

  return results.flat();
}

/**
 * Get public profiles for all players in a tournament
 *
 * @param tournamentId - The tournament ID
 * @returns Array of public profiles for tournament players
 */
export async function getPublicProfilesByTournament(
  tournamentId: string,
): Promise<PublicUser[]> {
  const playersQuery = query(
    collection(db, "publicProfiles"),
    where("tournamentIds", "array-contains", tournamentId),
  );

  const snapshot = await getDocs(playersQuery);
  return snapshot.docs.map((doc) =>
    formatFirestoreDocument(doc),
  ) as PublicUser[];
}

/**
 * Get public profiles for players across multiple tournaments
 * Batches requests to respect Firestore's "array-contains-any" limit of 30
 *
 * @param tournamentIds - Array of tournament IDs
 * @returns Array of unique public profiles (deduplicated)
 */
export async function getPublicProfilesByTournaments(
  tournamentIds: string[],
): Promise<PublicUser[]> {
  if (tournamentIds.length === 0) {
    return [];
  }

  const BATCH_SIZE = 30;
  const players: PublicUser[] = [];

  for (let i = 0; i < tournamentIds.length; i += BATCH_SIZE) {
    const batch = tournamentIds.slice(i, i + BATCH_SIZE);
    const playersQuery = query(
      collection(db, "publicProfiles"),
      where("tournamentIds", "array-contains-any", batch),
    );
    const snapshot = await getDocs(playersQuery);
    const batchPlayers = snapshot.docs.map((doc) =>
      formatFirestoreDocument(doc),
    ) as PublicUser[];
    players.push(...batchPlayers);
  }

  // Deduplicate players (in case a player appears in multiple batches)
  const uniquePlayers = Array.from(
    new Map(players.map((p) => [p.id, p])).values(),
  );

  return uniquePlayers;
}
